/**
 * Nombre de minutes dans une heure.
 */
const MINUTES_PER_HOUR = 60

/**
 * Nombre de quarts d'heure dans une heure (pas d'arrondi interne).
 */
const QUARTERS_PER_HOUR = 4

/**
 * Convertit un nombre de minutes en heures décimales, arrondies au quart
 * d'heure le plus proche (pas de 0.25).
 *
 * Règle métier (billing) :
 *   decimalHours = Math.round(minutes / 60 * 4) / 4
 *
 * Fonction pure, sans effet de bord.
 *
 * ⚠️ Distinction importante avec `roundUpQuarter` (module hours/) :
 * - `minutesToDecimalHours` utilise **Math.round** (arrondi au plus proche)
 *   pour la facturation client — neutre, symétrique autour du point médian.
 * - `roundUpQuarter` utilise **Math.ceil** (arrondi supérieur) pour la paie
 *   chauffeur — toujours en faveur du chauffeur.
 *
 * Les deux stratégies coexistent volontairement : ne pas les fusionner.
 *
 * Entrée :
 * - `minutes < 0` est clampé à 0 (sécurité : pas de durée négative).
 * - Les entrées fractionnaires (ex : 90.5) sont acceptées telles quelles,
 *   sans pré-arrondi à l'entier.
 * - Pas de garde contre NaN / Infinity : l'engine fait confiance aux types.
 *
 * Sortie :
 * - Toujours un multiple de 0.25.
 * - Toujours >= 0.
 *
 * @example
 *   minutesToDecimalHours(540)  // 9
 *   minutesToDecimalHours(90)   // 1.5
 *   minutesToDecimalHours(100)  // 1.75
 *   minutesToDecimalHours(8)    // 0.25
 *   minutesToDecimalHours(7)    // 0
 *   minutesToDecimalHours(-30)  // 0
 *
 * @param minutes - Durée en minutes.
 * @returns Durée en heures décimales, multiple de 0.25, >= 0.
 */
export function minutesToDecimalHours(minutes: number): number {
  if (minutes < 0) return 0
  return (
    Math.round((minutes / MINUTES_PER_HOUR) * QUARTERS_PER_HOUR) /
    QUARTERS_PER_HOUR
  )
}
